import type { GenericFieldOrder } from '@/hooks/invoice_reports/useShowSearchValue';
import { useTranslation } from '@/i18n';
import { useMemo } from 'react';

/**
 * FIELD_ORDERS drives the ActiveSearchTags display below the search form.
 * Keys must match the AdminInvoiceReportsSearchQuery field names (not form field names).
 * Dummy fields (applicant_ids, etc.) are not included because they are excluded from the query.
 */
export const useAdminInvoiceReportsFieldOrders = () => {
  const { t } = useTranslation();

  const FIELD_ORDERS: GenericFieldOrder[] = useMemo(
    () => [
      { key: 'query', label: t('admin_invoice_reports.search.keyword') },
      { key: 'number', label: t('admin_invoice_reports.search.number') },
      { key: 'status', label: t('admin_invoice_reports.search.status') },
      {
        key: 'office_member_ids',
        label: t('admin_invoice_reports.search.applicant'),
        concatChar: '、',
      },
      {
        key: 'dept_ids',
        label: t('admin_invoice_reports.search.applicant_department'),
        concatChar: '、',
      },
      {
        key: 'report_dept_ids',
        label: t('admin_invoice_reports.search.department'),
        concatChar: '、',
      },
      {
        key: 'ap_counterparty_ids',
        label: t('admin_invoice_reports.search.counterparty'),
        concatChar: '、',
      },
      {
        key: 'ap_payee_ids',
        label: t('admin_invoice_reports.search.ap_payee'),
        concatChar: '、',
      },
      {
        key: 'payment_method',
        label: t('admin_invoice_reports.search.payment_method'),
      },
      {
        key: ['total_amount_from', 'total_amount_to'],
        label: t('admin_invoice_reports.search.total_amount'),
        concatChar: ' ~ ',
        canShowPartialValue: true,
        isAmountField: true,
      },
      {
        key: 'payment_status',
        label: t('admin_invoice_reports.search.payment_status'),
      },
      { key: 'mf_file', label: t('admin_invoice_reports.search.mf_file') },
      {
        key: ['created_at_from', 'created_at_to'],
        label: t('admin_invoice_reports.search.created_date'),
        concatChar: ' - ',
        isDateField: true,
      },
      {
        key: ['submitted_at_from', 'submitted_at_to'],
        label: t('admin_invoice_reports.search.submitted_date'),
        concatChar: ' - ',
        isDateField: true,
      },
      {
        key: ['approved_at_from', 'approved_at_to'],
        label: t('admin_invoice_reports.search.approved_date'),
        concatChar: ' - ',
        isDateField: true,
      },
      {
        key: ['book_date_from', 'book_date_to'],
        label: t('admin_invoice_reports.search.book_date'),
        concatChar: ' - ',
        isDateField: true,
      },
      {
        key: ['due_date_from', 'due_date_to'],
        label: t('admin_invoice_reports.search.due_date'),
        concatChar: ' - ',
        isDateField: true,
      },
      {
        key: ['payment_date_from', 'payment_date_to'],
        label: t('admin_invoice_reports.search.payment_date'),
        concatChar: ' - ',
        isDateField: true,
        canShowPartialValue: true,
      },
    ],
    [t]
  );

  return { FIELD_ORDERS };
};
